import request from './request'
import { AxiosResponse } from 'axios'
import {
  DriverApplication,
  DriverCarInformation,
  DriverApplicationDocumentType,
  DriverApplicationCarPhotoType,
  DriverApplicationForm,
  DriverApplicationDocument,
  DocValidity,
  DriverApplicationList,
  DriverApplicationDocumentItem,
  DriverApplicationCarPhotoItem,
  DriverApplicationReport,
} from './types/driver-application'
import { DriverApplicationCarInfo } from './types/driver'

export const getDriverApplication = async (): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.get<DriverApplication>(`/driver-application/`)
  return data
}

export const getDriverApplicationCarInformation = async (
  applicationId: number,
): Promise<AxiosResponse<DriverCarInformation>> => {
  const data = await request.get<DriverCarInformation>(`/driver-application/${applicationId}/car-info`)
  return data
}

export const getDriverApplicationReport = async (
  applicationId: number,
): Promise<AxiosResponse<DriverApplicationReport>> => {
  const data = await request.get<DriverApplicationReport>(`/onfido/get-report/${applicationId}`)
  return data
}

export const getDriverApplicationDocument = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
): Promise<AxiosResponse<DriverApplicationDocument>> => {
  const data = await request.get<DriverApplicationDocument>(`/driver-application/${applicationId}/docs/${documentType}`)
  return data
}

export const getDriverApplicationCarPhoto = async (
  applicationId: number,
  photoType: DriverApplicationCarPhotoType,
): Promise<AxiosResponse<DriverApplicationDocument>> => {
  const data = await request.get<DriverApplicationDocument>(
    `/driver-application/${applicationId}/car-photos/${photoType}`,
  )
  return data
}

const DEFAULT_DRIVER_APPLICATION_NAME = 'default'

const withDefaultDriverApplicationNames = (data: Partial<DriverApplicationForm>): Partial<DriverApplicationForm> => ({
  ...data,
  first_name: data.first_name?.trim() || DEFAULT_DRIVER_APPLICATION_NAME,
  last_name: data.last_name?.trim() || DEFAULT_DRIVER_APPLICATION_NAME,
})

export const startDriverApplication = async (data: Partial<DriverApplicationForm>): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>('/driver-application/', withDefaultDriverApplicationNames(data))
  return response
}

export const updateDriverApplication = async (data: DriverApplicationForm): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>('/driver-application/', data)
  return response
}

export const submitDriverApplication = async (applicationId: number): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/driver-application/${applicationId}/submit`)
  return response
}

export const saveCarInformation = async (
  applicationId: number,
  data: DriverCarInformation,
): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/driver-application/${applicationId}/car-info`, data)
  return response
}

export const updateCarInformation = async (
  applicationId: number,
  data: DriverCarInformation,
): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>(`/driver-application/${applicationId}/car-info`, data)
  return response
}

// export const uploadDriverDocument = async (
//   applicationId: number,
//   file: File,
//   additionalData: { [key: string]: string },
// ): Promise<AxiosResponse<any>> => {
//   const formData = new FormData()
//   formData.append('file', file)

//   // Append additional data
//   Object.keys(additionalData).forEach((key) => {
//     formData.append(key, additionalData[key])
//   })

//   const response = await request.post<any>(`/driver-application/${applicationId}/submit`, formData, {
//     headers: {
//       'Content-Type': 'multipart/form-data',
//     },
//   })
//   return response
// }

export const uploadDriverApplicationDocument = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
  file: File,
): Promise<AxiosResponse<any>> => {
  const formData = new FormData()
  formData.append('document', file)

  const response = await request.post<any>(`/driver-application/${applicationId}/docs/${documentType}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const setDocValidity = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
  data: DocValidity,
): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>(`/driver-application/${applicationId}/docs/${documentType}`, data)
  return response
}

export const uploadDriverApplicationCarPhoto = async (
  applicationId: number,
  photoType: DriverApplicationCarPhotoType,
  file: File,
): Promise<AxiosResponse<any>> => {
  const formData = new FormData()
  formData.append('photo', file)

  const response = await request.post<any>(`/driver-application/${applicationId}/car-photos/${photoType}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const sendVerificationCode = async (phone: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/phone-verification/send-code/`, { phone_number: phone })
  return response
}

export const verifyCode = async (phone: string, code: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/phone-verification/verify-code/`, { phone_number: phone, code })
  return response
}

export const sendEmailVerificationCode = async (email: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/email-verification/send-code/`, { email })
  return response
}

export const verifyEmailCode = async (email: string, code: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/email-verification/verify-code/`, { email, code })
  return response
}

// ADMIN SECTION

export interface DriverApplicationsSearchParams {
  offset?: number
  limit?: number
  is_ready_for_review?: boolean
  is_sent_for_review?: boolean
  reviewed_by_id?: boolean
}

export const listAllDriverApplications = async ({
  offset,
  limit,
  is_ready_for_review,
  is_sent_for_review,
}: // reviewed_by_id,
DriverApplicationsSearchParams = {}): Promise<AxiosResponse<DriverApplicationList>> => {
  const params = new URLSearchParams()

  if (offset) params.append('offset', offset?.toString())
  if (limit) params.append('limit', limit?.toString())
  if (is_ready_for_review) {
    params.append('is_ready_for_review', is_ready_for_review ? 'true' : 'false')
    params.append('approved', 'false')
    params.append('rejected', 'false')
  }
  if (is_sent_for_review) {
    params.append('is_sent_for_review', is_sent_for_review ? 'true' : 'false')
    params.append('approved', 'false')
    params.append('rejected', 'false')
  }
  // if() params.append('reviewed_by_id', 'true')

  const data = await request.get<DriverApplicationList>(`/driver-applications/?${params.toString()}`)
  return data
}

export const getDriverApplicationById = async (id: number): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.get<DriverApplication>(`/driver-applications/${id}`)
  return data
}

export const getDriverApplicationCarInfoById = async (id: number): Promise<AxiosResponse<DriverApplicationCarInfo>> => {
  const data = await request.get<DriverApplicationCarInfo>(`/driver-applications/${id}/car-info/`)
  return data
}

export const getDriverApplicationCarPhotosById = async (
  id: number,
): Promise<AxiosResponse<DriverApplicationCarPhotoItem[]>> => {
  const data = await request.get<DriverApplicationCarPhotoItem[]>(`/driver-applications/${id}/car-photos/`)
  return data
}

export const getDriverApplicationDocumentsById = async (
  id: number,
): Promise<AxiosResponse<DriverApplicationDocumentItem[]>> => {
  const data = await request.get<DriverApplicationDocumentItem[]>(`/driver-applications/${id}/documents/`)
  return data
}

export const approveOrRejectDriverApplication = async (
  id: number,
  form: DriverApplication,
): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.patch<DriverApplication>(`/driver-applications/${id}/`, form)
  return data
}

export const deleteDriverApplicationAdmin = async (id: number): Promise<AxiosResponse<void>> => {
  return request.delete<void>(`/driver-applications/${id}/`)
}
